import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import { useLanguageContext } from '../contexts/LanguageContext';
import { interpolateParams } from '../i18n';

type ComputerDto = {
  id: string;
  machineName: string;
  os: 'mac' | 'windows' | 'linux' | string;
  activatedAt: string | null;
  osVersion?: string;
  cpuModel?: string;
  memoryGb?: number | null;
  hwModel?: string;
};

type ComputerListDto = {
  computers: ComputerDto[];
  activeCount: number;
  limit: number;
};

const osNames: Record<string, string> = { mac: 'macOS', windows: 'Windows', linux: 'Linux' };

const copy = {
  en: {
    screen_title: 'My Computers',
    intro: 'This page is where you manage which of your computers are active.',
    active_line: 'Computers active at the same time: {limit}.',
    free_line: 'You can activate this many more: {free}.',
    free: 'Free',
    full_other: 'All activations are in use. To activate another computer, open SonicMakers Installer on it and choose which computer to deactivate.',
    deactivate: 'Deactivate',
    deactivate_title: 'Deactivate the computer?',
    deactivate_hint: 'Installed plugins stay in place. One active place will be freed.',
    failed: 'Could not update the computer list.',
    activated: 'Activated',
    cancel: 'Cancel',
    loading: 'Loading...',
  },
  ru: {
    screen_title: 'Мои компьютеры',
    intro: 'На этой странице вы управляете активациями своих компьютеров.',
    active_line: 'Одновременно активных компьютеров — {limit}.',
    free_line: 'Можно активировать ещё — {free}.',
    free: 'Свободно',
    full_other: 'Все активации заняты. Чтобы активировать другой компьютер, запустите SonicMakers Installer на нём и выберите, какой компьютер деактивировать.',
    deactivate: 'Деактивировать',
    deactivate_title: 'Деактивировать компьютер?',
    deactivate_hint: 'Установленные плагины останутся на месте. Одно место освободится.',
    failed: 'Не удалось обновить список компьютеров.',
    activated: 'Активирован',
    cancel: 'Отмена',
    loading: 'Загрузка...',
  },
} as const;

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function osLine(item: ComputerDto): string {
  const base = osNames[item.os] || '';
  const version = String(item.osVersion || '').trim();
  return [base, version].filter(Boolean).join(' ');
}

function memoryLine(item: ComputerDto): string {
  const gb = Number(item.memoryGb);
  return Number.isFinite(gb) && gb > 0 ? `${gb} GB` : '';
}

function modelLine(item: ComputerDto): string {
  const model = String(item.hwModel || '').trim();
  if (!model || model === String(item.machineName || '').trim()) return '';
  return model;
}

function activatedLine(item: ComputerDto): string {
  const date = new Date(item.activatedAt || '');
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

const Computers: React.FC = () => {
  const { t, currentLanguage } = useLanguageContext();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [computers, setComputers] = useState<ComputerDto[]>([]);
  const [limit, setLimit] = useState(0);
  const [confirmId, setConfirmId] = useState('');
  const [confirmName, setConfirmName] = useState('');

  const text = (key: keyof typeof copy.en, params?: Record<string, string | number>) => {
    const translated = t(`computers.${key}`, params);
    if (translated && translated !== `computers.${key}`) return translated;
    const language = currentLanguage === 'ru' ? 'ru' : 'en';
    return interpolateParams(copy[language][key], params);
  };

  const fetchList = async () => {
    const res = await axios.get<ComputerListDto>(`/api/account/computers`, { headers: getAuthHeaders() });
    const list = res.data.computers || [];
    setComputers(list);
    setLimit(Number(res.data.limit ?? 0));
    return list;
  };

  const load = async () => {
    try {
      setFailed(false);
      await fetchList();
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const free = Math.max(0, limit - computers.length);
  const slotCount = Math.max(limit, 1);
  const rowStyle = { width: `calc(${slotCount} * 15rem + ${Math.max(slotCount - 1, 0)} * 0.75rem)` };

  const askDeactivate = (id: string, machineName: string) => {
    if (!id || busy) return;
    setConfirmId(id);
    setConfirmName(machineName || '');
  };

  const closeConfirm = () => {
    if (busy) return;
    setConfirmId('');
    setConfirmName('');
  };

  const confirmDeactivate = async () => {
    if (!confirmId || busy) return;
    setBusy(true);
    try {
      setFailed(false);
      const current = await fetchList();
      if (current.some((item) => item.id === confirmId)) {
        try {
          await axios.post(`/api/account/computers/${confirmId}/deactivate`, {}, { headers: getAuthHeaders() });
        } catch (err) {
          if (!axios.isAxiosError(err) || err.response?.status !== 404) throw err;
        }
        await fetchList();
      }
      setConfirmId('');
      setConfirmName('');
    } catch {
      setConfirmId('');
      setConfirmName('');
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#a855f7' }} />
          <p className="text-white mt-4">{text('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 text-white">
      <h1 className="text-3xl font-bold mb-6">{text('screen_title')}</h1>
      {failed ? (
        <div className="text-red-300">{text('failed')}</div>
      ) : (
        <>
          {limit > 0 && (
            <div className="mb-4 max-w-full rounded-lg border border-gray-700 bg-gray-800/70 px-4 py-2.5 text-xs leading-5 text-gray-400" style={rowStyle}>
              <p>{text('intro')}</p>
              <p>{text('active_line', { limit })}</p>
              {free > 0 ? (
                <p>{text('free_line', { free })}</p>
              ) : (
                <p className="text-yellow-300">{text('full_other')}</p>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <div className="grid gap-3 items-stretch" style={{ ...rowStyle, gridTemplateColumns: `repeat(${slotCount}, 15rem)` }}>
              {computers.map((computer) => (
                <div key={computer.id} className="h-full min-w-0 rounded-lg border border-gray-700 bg-gray-800/70 p-4 flex flex-col gap-3">
                  <div className="min-w-0 font-medium truncate" title={computer.machineName}>{computer.machineName}</div>
                  <div className="min-w-0 text-sm text-gray-300 space-y-1">
                    {modelLine(computer) && <div className="truncate" title={modelLine(computer)}>{modelLine(computer)}</div>}
                    {osLine(computer) && <div className="truncate" title={osLine(computer)}>{osLine(computer)}</div>}
                    {computer.cpuModel && <div className="truncate" title={computer.cpuModel}>{computer.cpuModel}</div>}
                    {memoryLine(computer) && <div className="truncate" title={memoryLine(computer)}>{memoryLine(computer)}</div>}
                    {activatedLine(computer) && (
                      <>
                        <div className="pt-1 text-xs text-gray-400">{text('activated')}</div>
                        <div className="truncate" title={activatedLine(computer)}>{activatedLine(computer)}</div>
                      </>
                    )}
                  </div>
                  <button
                    className="mt-auto self-start px-3 py-1 rounded bg-red-700 hover:bg-red-600 disabled:opacity-50 text-sm"
                    disabled={busy}
                    onClick={() => askDeactivate(computer.id, computer.machineName)}
                  >
                    {text('deactivate')}
                  </button>
                </div>
              ))}
              {Array.from({ length: free }, (_, index) => (
                <div key={`free-${index}`} className="h-full min-w-0 rounded-lg border border-dashed border-gray-700 px-4 py-3 text-sm text-gray-500">
                  {text('free')}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {confirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6" role="dialog" aria-modal="true">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 w-[360px] max-w-full text-center shadow-xl">
            <div className="text-lg font-semibold text-white mb-2">{text('deactivate_title')}</div>
            {confirmName && <div className="text-white text-sm mb-2 truncate" title={confirmName}>{confirmName}</div>}
            <div className="text-gray-400 text-sm mb-5">{text('deactivate_hint')}</div>
            <div className="flex items-center justify-center gap-3">
              <button className="px-4 py-2 rounded-md bg-red-700 hover:bg-red-600 disabled:opacity-50" disabled={busy} onClick={confirmDeactivate}>{text('deactivate')}</button>
              <button className="px-4 py-2 rounded-md border border-gray-700 text-gray-200 hover:bg-gray-800 disabled:opacity-50" disabled={busy} onClick={closeConfirm}>{text('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Computers;
